import { useState, useEffect, useCallback, useRef } from 'react';
import { useIonRouter } from '@ionic/react';
import { DataTable } from '../shared/DataTable';
import { AddPropertyModal } from '../shared/AddPropertyModal';
import { CreateCallModal } from '../calls/CreateCallModal';
import { searchProperties } from '../../services/propertyService';
import { searchBusinesses } from '../../services/businessService';
import { useRefresh } from '../../contexts/RefreshContext';
import type { Property } from '../../types/property';
import type { Column } from '../shared/DataTable';

const columns: Column<Property>[] = [
  { key: 'name', label: 'Name' },
  {
    key: 'propertyInfo',
    label: 'Property Info',
    render: (item) => (
      <div>
        <div>{item.address}</div>
        {item.toNumber && (
          <div className="text-xs text-gray-500 mt-0.5">
            To Number: {item.toNumber}
          </div>
        )}
        {item.websiteUrl && (
          <div className="text-xs text-gray-500 mt-0.5">
            Web Site URL:{' '}
            <a
              href={item.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {item.websiteUrl}
            </a>
          </div>
        )}
      </div>
    ),
  },
];

function RowMenu({ onEdit }: { onEdit: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[120px]">
          <button
            onClick={() => { setOpen(false); onEdit(); }}
            className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            Edit
          </button>
        </div>
      )}
    </div>
  );
}

interface PropertyListProps {
  title?: string;
  clientId?: string;
}

export function PropertyList({ title = 'Properties', clientId }: PropertyListProps) {
  const router = useIonRouter();

  const [data, setData] = useState<Property[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCreateCallModal, setShowCreateCallModal] = useState(false);
  const [editProperty, setEditProperty] = useState<Property | null>(null);
  const [teamsPropertyIds, setTeamsPropertyIds] = useState<Set<string>>(new Set());

  // Check which properties have pinned teams
  useEffect(() => {
    if (!clientId) return;
    searchBusinesses({ clientId }, { pageSize: 1000 }).then((businesses) => {
      setTeamsPropertyIds(new Set(businesses.filter(b => b.propertyId).map(b => b.propertyId!)));
    });
  }, [clientId]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const filter = clientId ? { clientId } : {};
      const results = await searchProperties(filter, { page, pageSize, orderBy: 'updated', order: 'desc' });
      setData(results);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, clientId]);

  useEffect(() => { fetch(); }, [fetch]);

  const refreshPath = `/properties-${clientId || 'all'}`;
  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  useEffect(() => {
    registerRefreshHandler(refreshPath, fetch);
    return () => unregisterRefreshHandler(refreshPath);
  }, [fetch, refreshPath, registerRefreshHandler, unregisterRefreshHandler]);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <DataTable
        title={title}
        columns={columns}
        data={data}
        page={page}
        pageSize={pageSize}
        loading={loading}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        onRefresh={fetch}
        onAdd={() => setShowAddModal(true)}
        headerControls={
          <button
            onClick={() => setShowCreateCallModal(true)}
            className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            title="Create Call"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
          </button>
        }
        actions={(item) => (
          <>
            {teamsPropertyIds.has(item.id) && (
              <button
                onClick={() => router.push(`/teams?clientId=${item.clientId}&propertyId=${item.id}`, 'none')}
                className="text-xs px-2 py-1 rounded text-white transition-colors" style={{ backgroundColor: 'var(--ion-color-secondary)' }}
              >
                Teams
              </button>
            )}
            <button
              onClick={() => router.push(`/agents?clientId=${item.clientId}&propertyId=${item.id}`, 'none')}
              className="text-xs px-2 py-1 rounded text-white transition-colors" style={{ backgroundColor: 'var(--ion-color-secondary)' }}
            >
              Agents
            </button>
            <button
              onClick={() => router.push(`/calls?propertyId=${item.id}${clientId ? `&clientId=${clientId}` : ''}`, 'none')}
              className="text-xs px-2 py-1 rounded text-white transition-colors" style={{ backgroundColor: 'var(--ion-color-secondary)' }}
            >
              Calls
            </button>
          </>
        )}
        rowMenu={(item) => (
          <RowMenu onEdit={() => setEditProperty(item)} />
        )}
      />
      {showAddModal && (
        <AddPropertyModal
          clientId={clientId}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => { setShowAddModal(false); fetch(); }}
        />
      )}
      {editProperty && (
        <AddPropertyModal
          property={editProperty}
          onClose={() => setEditProperty(null)}
          onSuccess={() => { setEditProperty(null); fetch(); }}
        />
      )}
      {showCreateCallModal && (
        <CreateCallModal
          clientId={clientId}
          onClose={() => setShowCreateCallModal(false)}
          onSuccess={() => setShowCreateCallModal(false)}
        />
      )}
    </div>
  );
}
